/**
 * What a repeat row is headed (SR-416, F17; the owner's Q19 (a)), and which two questions share a
 * line.
 *
 * **Words, not a mirror**: nothing on the wire names a repeat row's head — `RepeatParams` carries
 * no label — and survey-api's content designs call these heads rendering ("Team Member # X"). So
 * the table is this app's, keyed by usage code, and asked of survey-api as a layout hint that
 * would let it go (the survey review plan's D14). A usage it does not name keeps "Row N". A year
 * row's head is survey-api's own (`row_labels`, read by `multiYearRows`).
 */

const REPEAT_HEADS: Partial<Record<string, (ordinal: number) => string>> = {
  'TEAM-TM-001/members': (ordinal) => `Team Member # ${ordinal}`,
  // The survey review's T7 and T25: a revenue share's row is a team member's, a PI row an
  // organisation's (the plan's D14).
  'TEAM-TS-005': (ordinal) => `Team Member ${ordinal}`,
  'FA-PI-002/organisations': (ordinal) => `Organization ${ordinal}`,
  'FSP-PI-002/organisations': (ordinal) => `Organization ${ordinal}`,
  'TEAM-PI-005/organisations': (ordinal) => `Organization ${ordinal}`,
  'RIA-PI-006/organisations': (ordinal) => `Organization ${ordinal}`,
  'RIA-PC-001/principals': (ordinal) => `Principal/Senior Member # ${ordinal}`
};

/** A repeat row's head: its external key where it has one, else the group's words, else "Row N". */
export function repeatRowHead(
  groupCode: string,
  row: { externalKey: Nullable<string>; ordinal: Nullable<number> }
): string {
  if (row.externalKey) return row.externalKey;
  const words = REPEAT_HEADS[groupCode];
  return words && row.ordinal != null ? words(row.ordinal) : `Row ${row.ordinal ?? ''}`;
}

/**
 * A range's low end → its high end, drawn on one line from `sm` (the survey review's T21, R27;
 * the plan's D14). Words of this app's as the heads above are: nothing on the wire pairs them.
 */
const RANGE_PAIRS: Partial<Record<string, string>> = {
  'FA-CC-006': 'FA-CC-007',
  'FA-CC-008': 'FA-CC-009',
  'FSP-CC-006': 'FSP-CC-007',
  'TEAM-CC-005': 'TEAM-CC-006',
  'TEAM-CC-007': 'TEAM-CC-008',
  'RIA-CC-006': 'RIA-CC-007',
  'RIA-CC-008': 'RIA-CC-009'
};

/** The high end drawn beside a range's low end, if the usage is one. */
export function rangeHighOf(low: string): string | undefined {
  return RANGE_PAIRS[low];
}
